import React, {useState, useEffect} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import * as XLSX from 'xlsx';
import FournisseurPriceComparisonDashboard from './FournisseurPriceComparisonDashboard';
import FournisseurAchatsDashboard from './FournisseurAchatsDashboard';
import './FournisseursManagement.css';

const FournisseursManagement = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [stats, setStats] = useState({
    totalFournisseurs: 0,
    fournisseursActifs: 0,
    totalAchats: 0,
    evaluationMoyenne: '0.0'
  });

  // États pour les modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    statut: '',
    typeProduit: '',
    search: ''
  });

  const [types, setTypes] = useState([]);
  const [activeView, setActiveView] = useState('liste');

  // État pour le formulaire
  const [formData, setFormData] = useState({
    raisonSociale: '',
    nomContact: '',
    prenomContact: '',
    email: '',
    telephone: '',
    telephoneSecondaire: '',
    adresse: '',
    ville: '',
    codePostal: '',
    pays: 'Tunisie',
    matriculeFiscale: '',
    registreCommerce: '',
    typeProduit: [], // Array of ObjectIds
    specialites: [],
    siteWeb: '',
    notesCommentaires: '',
    conditionsPaiement: 'Net 30 jours',
    delaiLivraison: '',
    statut: 'Actif'
  });

  const API_URL = import.meta.env.VITE_API_URL;

  const getApiUrl = (endpoint) => {
    const baseUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
    return `${baseUrl}${endpoint}`;
  };

  const getTypeLabels = (fournisseur) => {
    if (!fournisseur || !Array.isArray(fournisseur.typeProduit) || fournisseur.typeProduit.length === 0) {
      return 'Non défini';
    }
    return fournisseur.typeProduit.map(t => t.label || t).join(', ');
  };

  // Vérification d'accès au montage (comme UsersManagement)
  useEffect(() => {
    const userType = localStorage.getItem('typeUser');
    if (userType !== 'admin' && userType !== 'moderateur') {
      onNavigate('profile');
      return;
    }
    // Si c'est un modérateur, vérifier la permission 'fournisseurs:read'
    if (userType === 'moderateur' && !hasPermission('fournisseurs', 'read')) {
      onNavigate('dashboard');
      return;
    }
    fetchFournisseurs();
    fetchStats();
    fetchTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/types-produit'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTypes(Array.isArray(response.data) ? response.data : (response.data?.data || []));
    } catch (err) {
      console.error('Erreur lors du chargement des types:', err);
    }
  };

  // Re-fetch quand les filtres changent
  useEffect(() => {
    fetchFournisseurs();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFournisseurs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.typeProduit) params.append('typeProduit', filters.typeProduit);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(getApiUrl(`/fournisseurs?${params}`), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setFournisseurs(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des fournisseurs:', err);
      setError('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/fournisseurs/stats'), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const {name, value} = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdd = () => {
    setFormData({
      raisonSociale: '',
      nomContact: '',
      prenomContact: '',
      email: '',
      telephone: '',
      telephoneSecondaire: '',
      adresse: '',
      ville: '',
      codePostal: '',
      pays: 'Tunisie',
      matriculeFiscale: '',
      registreCommerce: '',
      typeProduit: [],
      specialites: [],
      siteWeb: '',
      notesCommentaires: '',
      conditionsPaiement: 'Net 30 jours',
      delaiLivraison: '',
      statut: 'Actif'
    });
    setShowAddModal(true);
  };

  const handleViewDetails = (fournisseur) => {
    // Navigation vers la page de détail
    onNavigate('fournisseur-detail', {fournisseurId: fournisseur._id});
  };

  const handleEdit = (fournisseur) => {
    setSelectedFournisseur(fournisseur);
    setFormData({
      raisonSociale: fournisseur.raisonSociale,
      nomContact: fournisseur.nomContact,
      prenomContact: fournisseur.prenomContact || '',
      email: fournisseur.email,
      telephone: fournisseur.telephone,
      telephoneSecondaire: fournisseur.telephoneSecondaire || '',
      adresse: fournisseur.adresse,
      ville: fournisseur.ville,
      codePostal: fournisseur.codePostal || '',
      pays: fournisseur.pays || 'Tunisie',
      matriculeFiscale: fournisseur.matriculeFiscale || '',
      registreCommerce: fournisseur.registreCommerce || '',
      typeProduit: (fournisseur.typeProduit || []).map(t => t._id || t),
      specialites: fournisseur.specialites || [],
      siteWeb: fournisseur.siteWeb || '',
      notesCommentaires: fournisseur.notesCommentaires || '',
      conditionsPaiement: fournisseur.conditionsPaiement || 'Net 30 jours',
      delaiLivraison: fournisseur.delaiLivraison || '',
      statut: fournisseur.statut
    });
    setShowEditModal(true);
  };

  const handleDelete = (fournisseur) => {
    setSelectedFournisseur(fournisseur);
    setShowDeleteModal(true);
  };

  const handleDetail = (fournisseur) => {
    onNavigate('fournisseur-detail', fournisseur._id);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl('/fournisseurs'), formData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowAddModal(false);
      fetchFournisseurs();
      fetchStats();
      setMessage('Fournisseur ajouté avec succès');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de l\'ajout:', err);
      if (err.response?.status === 409) {
        setError('Cet email ou matricule fiscal existe déjà');
      } else {
        setError('Erreur lors de l\'ajout du fournisseur');
      }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.put(getApiUrl(`/fournisseurs/${selectedFournisseur._id}`), formData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowEditModal(false);
      setSelectedFournisseur(null);
      fetchFournisseurs();
      fetchStats();
      setMessage('Fournisseur modifié avec succès');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      setError('Erreur lors de la modification du fournisseur');
    }
  };

  const handleDeleteConfirm = async () => {
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(getApiUrl(`/fournisseurs/${selectedFournisseur._id}`), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowDeleteModal(false);
      setSelectedFournisseur(null);
      fetchFournisseurs();
      fetchStats();
      setMessage('Fournisseur supprimé avec succès');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression du fournisseur');
    }
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedFournisseur(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND'
    }).format(value);
  };

  const getStatutBadgeClass = (statut) => {
    switch (statut) {
      case 'Actif': return 'statut-actif';
      case 'Inactif': return 'statut-inactif';
      case 'En attente': return 'statut-attente';
      case 'Bloqué': return 'statut-bloque';
      default: return '';
    }
  };

  const handleExportExcel = () => {
    const exportData = fournisseurs.map(f => ({
      'ID FOURNISSEUR': f._id || '',
      'RAISON SOCIALE': f.raisonSociale || '',
      'NOM CONTACT': f.nomContact || '',
      'PRÉNOM CONTACT': f.prenomContact || '',
      'EMAIL': f.email || '',
      'TÉLÉPHONE': f.telephone || '',
      'TÉLÉPHONE SECONDAIRE': f.telephoneSecondaire || '',
      'ADRESSE': f.adresse || '',
      'VILLE': f.ville || '',
      'CODE POSTAL': f.codePostal || '',
      'PAYS': f.pays || '',
      'MATRICULE FISCALE': f.matriculeFiscale || '',
      'REGISTRE COMMERCE': f.registreCommerce || '',
      'ID TYPE PRODUITS': f.typeProduit ? (Array.isArray(f.typeProduit) ? f.typeProduit.map(t => t._id || t).join(', ') : f.typeProduit) : '',
      'TYPE PRODUIT': getTypeLabels(f) || '',
      'SPÉCIALITÉS': f.specialites ? f.specialites.join(', ') : '',
      'SITE WEB': f.siteWeb || '',
      'CONDITIONS PAIEMENT': f.conditionsPaiement || '',
      'DÉLAI LIVRAISON': f.delaiLivraison || '',
      'STATUT': f.statut || '',
      'EVALUATION - QUALITÉ': f.evaluation?.qualite || 0,
      'EVALUATION - PRIX': f.evaluation?.prix || 0,
      'EVALUATION - DÉLAI': f.evaluation?.delai || 0,
      'EVALUATION - SERVICE': f.evaluation?.service || 0,
      'TOTAL ACHATS': f.totalAchats || 0,
      'NOMBRE COMMANDES': f.nombreCommandes || 0,
      'DERNIÈRE COMMANDE': f.derniereCommande ? new Date(f.derniereCommande).toLocaleDateString('fr-FR') : '',
      'NOTES': f.notesCommentaires || '',
      'DATE DE CRÉATION': f.createdAt ? new Date(f.createdAt).toLocaleDateString('fr-FR') : ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Fournisseurs');
    XLSX.writeFile(workbook, 'Fournisseurs.xlsx');
  };

  return (
    <div className="fournisseurs-management-container">
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />
      <div className="fournisseurs-management-content">
        <div className="dashboard-header">
          <div>
            <h1>Gestion des Fournisseurs</h1>
            <p>Administration des fournisseurs et historiques</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn-add-fournisseur" style={{backgroundColor: '#10b981'}} onClick={handleExportExcel}>
              <i className="fas fa-file-excel"></i> Exporter
            </button>
            {hasPermission('fournisseurs', 'create') && (
              <button className="btn-add-fournisseur" onClick={handleAdd}>
                <i className="fas fa-plus"></i> Ajouter un fournisseur
              </button>
            )}
          </div>
        </div>

        <div className="fm-view-switch">
          <button className={activeView === 'liste' ? 'active' : ''} onClick={() => setActiveView('liste')}>
            <i className="fas fa-list"></i> Liste fournisseurs
          </button>
          <button className={activeView === 'comparaison' ? 'active' : ''} onClick={() => setActiveView('comparaison')}>
            <i className="fas fa-scale-balanced"></i> Comparaison prix
          </button>
          <button className={activeView === 'achats' ? 'active' : ''} onClick={() => setActiveView('achats')}>
            <i className="fas fa-chart-pie"></i> Dashboard achats
          </button>
        </div>

        {activeView === 'comparaison' ? (
          <FournisseurPriceComparisonDashboard
            getApiUrl={getApiUrl}
            types={types}
            onNavigate={onNavigate}
          />
        ) : activeView === 'achats' ? (
          <FournisseurAchatsDashboard
            getApiUrl={getApiUrl}
            fournisseurs={fournisseurs}
            onOpenFournisseur={(fournisseurId) => onNavigate('fournisseur-detail', fournisseurId)}
            onCreateFactureFromBl={(row) => onNavigate('fournisseur-detail', {
              fournisseurId: row.fournisseur?.id,
              action: 'create_facture_from_bl',
              blId: row.id
            })}
            onCreateBlFromBc={(row) => onNavigate('fournisseur-detail', {
              fournisseurId: row.fournisseur?.id,
              action: 'create_bl_from_bc',
              bcId: row.id
            })}
          />
        ) : (
        <>
        {/* Cartes de statistiques */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon blue">
              <i className="fas fa-truck"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.totalFournisseurs}</h3>
              <p>Total Fournisseurs</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.fournisseursActifs}</h3>
              <p>Fournisseurs Actifs</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange">
              <i className="fas fa-shopping-cart"></i>
            </div>
            <div className="stat-info">
              <h3>{formatCurrency(stats.totalAchats)}</h3>
              <p>Total Achats</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon purple">
              <i className="fas fa-star"></i>
            </div>
            <div className="stat-info">
              <h3>{stats.evaluationMoyenne}/5</h3>
              <p>Évaluation Moyenne</p>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="filters-section">
          <div className="filter-group">
            <label>Recherche</label>
            <input
              type="text"
              name="search"
              placeholder="Rechercher par nom, email..."
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label>Statut</label>
            <select name="statut" value={filters.statut} onChange={handleFilterChange}>
              <option value="">Tous</option>
              <option value="Actif">Actif</option>
              <option value="Inactif">Inactif</option>
              <option value="En attente">En attente</option>
              <option value="Bloqué">Bloqué</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Type</label>
            <select name="typeProduit" value={filters.typeProduit} onChange={handleFilterChange}>
              <option value="">Tous</option>
              {types.map(t => (
                <option key={t._id} value={t._id}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        <div className="fournisseurs-table-container">
          {loading ? (
            <LoadingSpinner message="Chargement des fournisseurs..." />
          ) : (
            <table className="fournisseurs-table">
              <thead>
                <tr>
                  <th>Raison Sociale</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Type</th>
                  <th>Ville</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fournisseurs.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{textAlign: 'center', padding: '2rem', color: '#888'}}>
                      Aucun fournisseur trouvé
                    </td>
                  </tr>
                ) : (
                  fournisseurs.map((fournisseur) => (
                    <tr key={fournisseur._id}>
                      <td>
                        <strong>{fournisseur.raisonSociale}</strong>
                      </td>
                      <td>{fournisseur.prenomContact} {fournisseur.nomContact}</td>
                      <td>{fournisseur.email}</td>
                      <td>{fournisseur.telephone}</td>
                      <td>
                        <span
                          className={`fournisseur-type-badge${getTypeLabels(fournisseur) === 'Non défini' ? ' is-missing' : ''}`}
                          title={getTypeLabels(fournisseur)}
                        >
                          {getTypeLabels(fournisseur)}
                        </span>
                      </td>
                      <td>{fournisseur.ville}</td>
                      <td>
                        <span className={`statut-badge ${getStatutBadgeClass(fournisseur.statut)}`}>
                          {fournisseur.statut}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-detail" onClick={() => handleDetail(fournisseur)} title="Détails & Historique">
                            <i className="fas fa-eye"></i>
                          </button>
                          <button className="btn-edit" onClick={() => handleEdit(fournisseur)} title="Modifier">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn-delete" onClick={() => handleDelete(fournisseur)} title="Supprimer">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Modales d'ajout et édition */}
        </>
        )}

        {(showAddModal || showEditModal) && (
          <div className="modal-overlay" onClick={closeModals}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{showAddModal ? 'Ajouter un fournisseur' : 'Modifier le fournisseur'}</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <div className="modal-body">
                <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
                  <div className="form-section">
                    <h3>Informations Générales</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Raison Sociale *</label>
                        <input
                          type="text"
                          name="raisonSociale"
                          value={formData.raisonSociale}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Types de Produit *</label>
                        <div className="types-produit-checkboxes" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', maxHeight: '150px', overflowY: 'auto' }}>
                          {types.map(t => (
                            <label key={t._id} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={formData.typeProduit.includes(t._id)}
                                onChange={(e) => {
                                  const id = t._id;
                                  setFormData(prev => ({
                                    ...prev,
                                    typeProduit: e.target.checked 
                                      ? [...prev.typeProduit, id] 
                                      : prev.typeProduit.filter(x => x !== id)
                                  }));
                                }}
                              />
                              {t.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Contact</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom Contact *</label>
                        <input
                          type="text"
                          name="nomContact"
                          value={formData.nomContact}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Prénom Contact</label>
                        <input
                          type="text"
                          name="prenomContact"
                          value={formData.prenomContact}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Téléphone *</label>
                        <input
                          type="tel"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Téléphone Secondaire</label>
                      <input
                        type="tel"
                        name="telephoneSecondaire"
                        value={formData.telephoneSecondaire}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Adresse</h3>
                    <div className="form-group">
                      <label>Adresse *</label>
                      <input
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Ville *</label>
                        <input
                          type="text"
                          name="ville"
                          value={formData.ville}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Code Postal</label>
                        <input
                          type="text"
                          name="codePostal"
                          value={formData.codePostal}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Pays</label>
                      <input
                        type="text"
                        name="pays"
                        value={formData.pays}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Informations Légales</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Matricule Fiscal</label>
                        <input
                          type="text"
                          name="matriculeFiscale"
                          value={formData.matriculeFiscale}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Registre Commerce</label>
                        <input
                          type="text"
                          name="registreCommerce"
                          value={formData.registreCommerce}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Autres Informations</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Site Web</label>
                        <input
                          type="url"
                          name="siteWeb"
                          value={formData.siteWeb}
                          onChange={handleInputChange}
                          placeholder="https://"
                        />
                      </div>
                      <div className="form-group">
                        <label>Statut</label>
                        <select
                          name="statut"
                          value={formData.statut}
                          onChange={handleInputChange}
                        >
                          <option value="Actif">Actif</option>
                          <option value="Inactif">Inactif</option>
                          <option value="En attente">En attente</option>
                          <option value="Bloqué">Bloqué</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Conditions de Paiement</label>
                        <input
                          type="text"
                          name="conditionsPaiement"
                          value={formData.conditionsPaiement}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Délai de Livraison</label>
                        <input
                          type="text"
                          name="delaiLivraison"
                          value={formData.delaiLivraison}
                          onChange={handleInputChange}
                          placeholder="Ex: 2 semaines"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Notes / Commentaires</label>
                      <textarea
                        name="notesCommentaires"
                        value={formData.notesCommentaires}
                        onChange={handleInputChange}
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                    <button type="submit" className="btn-save">
                      {showAddModal ? 'Ajouter' : 'Enregistrer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modale de suppression */}
        {showDeleteModal && selectedFournisseur && (
          <div className="modal-overlay" onClick={closeModals}>
            <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Confirmer la suppression</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <div className="modal-body">
                <p>Êtes-vous sûr de vouloir supprimer le fournisseur <strong>{selectedFournisseur.raisonSociale}</strong> ?</p>
                <p className="warning-text">Cette action supprimera également tout l'historique associé et est irréversible.</p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                <button type="button" className="btn-delete-confirm" onClick={handleDeleteConfirm}>Supprimer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FournisseursManagement;
